import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { CartItem, Meal, SelectedAddon } from '@/types/meal';
import { useDeliverySettings } from '@/hooks/useDeliverySettings';

const CART_STORAGE_KEY = 'barbor-cart';

interface CartContextType {
  items: CartItem[];
  addItem: (meal: Meal, removedIngredients?: string[], selectedAddons?: SelectedAddon[], notes?: string) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  deliveryFee: number;
  freeDeliveryThreshold: number;
  amountUntilFreeDelivery: number;
  cartBounce: boolean;
  settingsLoading: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

function loadCartFromStorage(): CartItem[] {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return [];
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(loadCartFromStorage);
  const [cartBounce, setCartBounce] = useState(false);
  const { data: settings, isLoading: settingsLoading } = useDeliverySettings();

  const freeDeliveryEnabled = settings?.freeDeliveryEnabled ?? true;
  const freeDeliveryThreshold = settings?.freeDeliveryThreshold ?? 10000;
  const baseFee = settings?.deliveryFee ?? 2500;

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((
    meal: Meal,
    removedIngredients: string[] = [],
    selectedAddons: SelectedAddon[] = [],
    notes?: string
  ) => {
    const newItem: CartItem = {
      id: `${meal.id}-${Date.now()}`,
      meal,
      quantity: 1,
      removedIngredients,
      selectedAddons,
      notes,
    };
    setItems((prev) => [...prev, newItem]);
    setCartBounce(true);
    setTimeout(() => setCartBounce(false), 600);
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    if (quantity < 1) {
      setItems((prev) => prev.filter((item) => item.id !== itemId));
      return;
    }
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, quantity } : item))
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    localStorage.removeItem(CART_STORAGE_KEY);
  }, []);

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const totalPrice = items.reduce((sum, item) => {
    const addonTotal = item.selectedAddons.reduce((s, addon) => s + addon.priceInCents, 0);
    return sum + (item.meal.priceInCents + addonTotal) * item.quantity;
  }, 0);

  const deliveryFee =
    freeDeliveryEnabled && totalPrice >= freeDeliveryThreshold ? 0 : baseFee;

  const amountUntilFreeDelivery = freeDeliveryEnabled
    ? Math.max(0, freeDeliveryThreshold - totalPrice)
    : 0;

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
        deliveryFee,
        freeDeliveryThreshold,
        amountUntilFreeDelivery,
        cartBounce,
        settingsLoading,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
